import logger from '../../../../utils/logging/logger';

// Pure total-price calculations for usePaymentForm, extracted VERBATIM. Two
// distinct call sites (the displayed total, and the amount createPaymentIntent
// sends to the backend) had two independently-evolved implementations in the
// original — courses defensive-array-extraction differs between them — so
// per this session's disclosed-duplication-preservation convention they stay
// as two separate functions rather than being consolidated.

// FIX #9: Memoize total price calculation to prevent unnecessary recalculations
// FIX #23: Add price precision handling to prevent floating point errors
export const computeDisplayTotalPrice = ({ courses, bulkPricing, appliedPromo, productIds }) => {
  // Calculate base total from courses/products
  // Use isPaidCourse check to match the subtotal display logic
  // FIX #22: Clear separation - handle courses and products the same way (products are converted to course-like format)
  // FIX: Add comprehensive null safety check for courses array
  // Handle case where courses might be an object with a courses property
  let coursesArray = null;

  // First check if courses is already an array
  if (Array.isArray(courses)) {
    coursesArray = courses;
  }
  // If it's an object, try to extract the array from common property names
  else if (courses && typeof courses === 'object') {
    // Try common property names where the array might be stored
    if (courses.courses && Array.isArray(courses.courses)) {
      coursesArray = courses.courses;
      logger.debug('getTotalPrice: extracted courses array from courses.courses property', {
        originalKeys: Object.keys(courses),
        arrayLength: coursesArray.length
      });
    } else if (courses.data && Array.isArray(courses.data)) {
      coursesArray = courses.data;
      logger.debug('getTotalPrice: extracted courses array from courses.data property');
    } else if (courses.items && Array.isArray(courses.items)) {
      coursesArray = courses.items;
      logger.debug('getTotalPrice: extracted courses array from courses.items property');
    } else {
      // Log detailed info for debugging
      logger.warn('getTotalPrice called with invalid courses (object without array property)', {
        courses,
        type: typeof courses,
        isArray: Array.isArray(courses),
        keys: courses ? Object.keys(courses) : null,
        hasCoursesProp: courses ? 'courses' in courses : false,
        coursesPropType: courses && courses.courses ? typeof courses.courses : null
      });
      return 0;
    }
  }
  // If courses is null/undefined, silently return 0 (normal during initial load)
  else if (courses === null || courses === undefined) {
    return 0;
  }
  // Any other type is invalid
  else {
    logger.warn('getTotalPrice called with invalid courses type', {
      courses,
      type: typeof courses
    });
    return 0;
  }

  // Final validation of the extracted array
  if (!coursesArray || !Array.isArray(coursesArray)) {
    // Only warn if we actually had a non-empty value (not just empty during initial load)
    const hasNonEmptyValue = courses !== null &&
                             courses !== undefined &&
                             !(Array.isArray(courses) && courses.length === 0);
    if (hasNonEmptyValue) {
      logger.warn('getTotalPrice: extracted array is invalid', {
        coursesArray,
        originalCourses: courses,
        type: typeof coursesArray,
        isArray: Array.isArray(coursesArray),
        length: coursesArray ? coursesArray.length : null
      });
    }
    return 0;
  }

  // Empty array is valid (just means no courses, total is 0)
  if (coursesArray.length === 0) {
    return 0;
  }

  const baseTotal = coursesArray.reduce((sum, course) => {
    // FIX: Add null check for course object
    if (!course) {
      logger.warn('Invalid course object in courses array', { course });
      return sum;
    }
    // Handle both courses and products (products are already converted to course-like format in fetchData)
    // Check if course is paid before including price
    const price = (course.isPaidCourse || course.is_paid_course) ? (parseFloat(course.price) || 0) : 0;
    // FIX #23: Use Math.round to prevent floating point precision errors
    // Multiply by 100, add, then divide by 100 to maintain 2 decimal precision
    return Math.round((sum + price) * 100) / 100;
  }, 0);

  logger.debug('getTotalPrice calculation', {
    baseTotal,
    coursesCount: coursesArray.length,
    courses: (coursesArray && Array.isArray(coursesArray)) ? coursesArray.filter(c => c && c.id).map(c => ({ id: c.id, name: c.name, price: c.price, isPaidCourse: c.isPaidCourse || c.is_paid_course })) : [],
    hasBulkPricing: !!bulkPricing,
    hasAppliedPromo: !!appliedPromo,
    bulkPricingData: bulkPricing ? {
      finalPrice: bulkPricing.pricing?.finalPrice,
      discountAmount: bulkPricing.pricing?.discountAmount,
      originalPrice: bulkPricing.pricing?.originalPrice
    } : null
  });

  // Apply EITHER bulk pricing OR promo code discount (mutually exclusive)
  // Priority: Promo code takes precedence over bulk pricing
  let finalTotal = baseTotal;

  if (appliedPromo) {
    // Apply promo code discount (promo code takes priority)
    // FIX #4: Add precision handling to prevent floating point errors (consistent with bulk pricing)
    if (appliedPromo.discount_type === 'percentage') {
      const discountValue = parseFloat(appliedPromo.discount_value) || 0;
      const discountAmount = baseTotal * (discountValue / 100);
      // FIX #4: Use Math.round to prevent floating point precision errors
      finalTotal = Math.max(0, Math.round((baseTotal - discountAmount) * 100) / 100);
      logger.debug('Applying percentage promo code discount', {
        baseTotal,
        discountValue,
        discountAmount,
        finalTotal,
        discountType: appliedPromo.discount_type,
        promoCode: appliedPromo.code
      });
    } else {
      const discountValue = parseFloat(appliedPromo.discount_value) || 0;
      // FIX #4: Use Math.round to prevent floating point precision errors
      finalTotal = Math.max(0, Math.round((baseTotal - discountValue) * 100) / 100);
      logger.debug('Applying fixed amount promo code discount', {
        baseTotal,
        discountValue,
        finalTotal,
        discountType: appliedPromo.discount_type,
        promoCode: appliedPromo.code
      });
    }
  } else if (bulkPricing && bulkPricing?.pricing?.finalPrice && !productIds) {
    // FIX #6: Apply bulk pricing discount only if no promo code is applied (with null check)
    // Use optional chaining for consistency and safety
    finalTotal = bulkPricing?.pricing?.finalPrice || baseTotal;
    logger.debug('Applying bulk pricing discount', {
      baseTotal,
      finalTotal,
      discountAmount: bulkPricing?.pricing?.discountAmount,
      originalPrice: bulkPricing?.pricing?.originalPrice,
      savings: bulkPricing?.pricing?.savings
    });
  } else {
    logger.debug('No discount applied', { baseTotal, finalTotal, hasBulkPricing: !!bulkPricing, hasAppliedPromo: !!appliedPromo });
  }

  return finalTotal;
};

// The amount createPaymentIntent sends to the backend — computed from
// coursesRef-resolved "current" values rather than getTotalPrice's more
// defensive array-extraction, so it's kept as its own function rather than
// sharing computeDisplayTotalPrice's body.
export const computeCurrentTotalAmount = ({ courses, appliedPromo, bulkPricing, productIds }) => {
  const baseTotal = courses.reduce((sum, course) => {
    const price = (course.isPaidCourse || course.is_paid_course) ? (parseFloat(course.price) || 0) : 0;
    return Math.round((sum + price) * 100) / 100;
  }, 0);

  console.log('[FREE_ENROLLMENT] Calculating totalAmount in createPaymentIntent', {
    baseTotal,
    hasPromo: !!appliedPromo,
    promoCode: appliedPromo?.code,
    promoType: appliedPromo?.discount_type,
    promoValue: appliedPromo?.discount_value,
    hasBulkPricing: !!bulkPricing,
    courseCount: courses.length
  });

  if (appliedPromo) {
    if (appliedPromo.discount_type === 'percentage') {
      const discountValue = parseFloat(appliedPromo.discount_value) || 0;
      const discountAmount = baseTotal * (discountValue / 100);
      const finalAmount = Math.max(0, Math.round((baseTotal - discountAmount) * 100) / 100);
      console.log('[FREE_ENROLLMENT] Applied percentage discount', {
        baseTotal,
        discountValue,
        discountAmount,
        finalAmount,
        isZero: finalAmount === 0
      });
      return finalAmount;
    } else {
      const discountValue = parseFloat(appliedPromo.discount_value) || 0;
      const finalAmount = Math.max(0, Math.round((baseTotal - discountValue) * 100) / 100);
      console.log('[FREE_ENROLLMENT] Applied fixed discount', {
        baseTotal,
        discountValue,
        finalAmount,
        isZero: finalAmount === 0
      });
      return finalAmount;
    }
  } else if (bulkPricing && bulkPricing?.pricing?.finalPrice && !productIds) {
    // FIX: Use currentProductIds instead of productIds from closure
    const finalAmount = Math.round(parseFloat(bulkPricing.pricing.finalPrice) * 100) / 100;
    console.log('[FREE_ENROLLMENT] Using bulk pricing', { finalAmount, isZero: finalAmount === 0 });
    return finalAmount;
  }
  console.log('[FREE_ENROLLMENT] No discount applied, using baseTotal', { baseTotal, isZero: baseTotal === 0 });
  return baseTotal;
};
